import { Avatar, Button, Card, CardActions, CardContent, Typography, CardHeader, Divider, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemText, Dialog, DialogContent, IconButton, DialogTitle, Box, DialogActions, Tooltip, Alert, LinearProgress, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import { Order } from "../../../../app/models/order";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { currencyFormat, formatToLocalDate, formatToLocalTime, getOrderStatusColor, getOrderStatusTitle, getPolishLiteral } from "../../../../app/util/util";
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/Error';
import DoneIcon from '@mui/icons-material/Done';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import agent from "../../../../app/api/agent";
import { keyframes } from '@emotion/react';
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import AdminOrderScanUploadForm from "../AdminOrderScanUploadForm";
import { useAppDispatch, useAppSelector } from "../../../../app/store/configureStore";
import { toast } from "react-toastify";
import { fetchAdminOrdersAsync } from "../adminOrdersSlice";
import AdminOrderInvoiceUrlDialog from "../AdminOrderInvoiceUrlDialog";

interface Props {
    order: Order;
    handleCardSelectOrder?: (handledOrder: Order) => void;
    handleCardSelectAddInvoice?: (handledOrder: Order) => void;
}

export default function AdminOrderCard({ order, handleCardSelectOrder } : Props) {
    const benefitients = order.benefitients ? order.benefitients.split(';').map(item => {
        const [lastName, firstName, pesel, birthPlace, birthDate, certId] = item.split('~');
        return {
            lastName, firstName, pesel, birthPlace, birthDate,
            certId: certId !== undefined ? certId : ''
        };
    }) : [];

    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.account);
    const isSuperAdmin = user?.roles?.includes('SuperAdmin');

    const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
    // const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
    const [isDeletingOrder, setIsDeletingOrder] = useState(false);
    const [isDownloadingTest, setIsDownloadingTest] = useState(false);
    const [isDownloadingKey, setIsDownloadingKey] = useState(false);
    const [open, setOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [invoiceUrlDialogOpen, setInvoiceUrlDialogOpen] = useState(false);

    function getStatusIcon(currentState: number) {
        switch (currentState) {
            case 0:
                return <HourglassTopIcon />;
            case 1:
                return <HourglassTopIcon />;
            case 2:
                return <DoneIcon />;
            case 3:
                return <DoneIcon />;
            case 4:
                return <HelpOutlineIcon />;
            case 5:
                return <CancelIcon />;
            case 6:
                return <HourglassTopIcon />; // APPROVED_WITHOUT_SCANS
            case 7:
                return <DoneIcon />; // SCANS_ADDED_AFTER_APPROVAL
            default:
                return <ErrorIcon />;
        }
    }

    const downloadBlob = (blob: Blob, fileName: string) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    async function handleGenerateCertificates(orderId: number) {
        setIsGeneratingCertificate(true);
        try {
            const response = await agent.GenerateCertificate.generateCertificate(orderId);

            if (response.data instanceof Blob) {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                downloadBlob(blob, `Certyfikaty_${orderId}.pdf`);
            } else {
                throw new Error('Nieprawidłowa odpowiedź z serwera');
            }
        } catch (error: any) {
            console.error('Błąd podczas generowania certyfikatów:', error);

            if (error.response?.data?.title) {
                toast.error(error.response.data.title);
            } else {
                toast.error('Wystąpił błąd podczas generowania certyfikatów');
            }
        } finally {
            setIsGeneratingCertificate(false);
        }
    }

    // Funkcje pomocnicze do sprawdzania dostępności danych testu i klucza
    const hasTestData = () => (order.isGraphical && order.packageTestPath) || (!order.isGraphical && order.questionsJson && order.questionsJson !== "[]" && order.questionsJson !== "{}");
    const hasAnswerKeyData = () => (order.isGraphical && order.packageTestKeyPath) || (!order.isGraphical && order.questionsJson && order.questionsJson !== "[]" && order.questionsJson !== "{}");

    async function handleDownloadTestPdf() {
        setIsDownloadingTest(true);
        try {
            await agent.Admin.Orders.generateTestPdf(order.publicId);
            toast.success('Test został pomyślnie pobrany');
        } catch (error: any) {
            console.error('Błąd podczas pobierania testu:', error);
            if (error.response?.data?.title) {
                toast.error(error.response.data.title);
            } else {
                toast.error('Nie udało się pobrać testu');
            }
        } finally {
            setIsDownloadingTest(false);
        }
    }

    async function handleDownloadAnswerKeyPdf() {
        setIsDownloadingKey(true);
        try {
            await agent.Admin.Orders.generateAnswerKeyPdf(order.publicId);
            toast.success('Klucz odpowiedzi został pomyślnie pobrany');
        } catch (error: any) {
            console.error('Błąd podczas pobierania klucza odpowiedzi:', error);
            if (error.response?.data?.title) {
                toast.error(error.response.data.title);
            } else {
                toast.error('Nie udało się pobrać klucza odpowiedzi');
            }
        } finally {
            setIsDownloadingKey(false);
        }
    }

    // async function handleGenerateInvoice() {
    //     setIsGeneratingInvoice(true);
    //     try {
    //         const response = await agent.BizInApi.apiTestConnection();
    //         console.log(response);
    //         toast(response);
    //     } catch (error) {
    //         console.error("Błąd podczas generowania faktury", error);
    //     }
    //     setIsGeneratingInvoice(false);
    // }

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDeleteDialogOpen = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
    };

    const handleDeleteOrder = async () => {
        setIsDeletingOrder(true);
        try {
            await agent.Admin.Orders.delete(order.publicId);
            toast.success(`Zamówienie #${order.publicId} zostało usunięte`);
            dispatch(fetchAdminOrdersAsync());
        } catch (error) {
            console.error("Błąd podczas usuwania zamówienia", error);
            toast.error("Wystąpił błąd podczas usuwania zamówienia");
        } finally {
            setIsDeletingOrder(false);
            handleDeleteDialogClose();
        }
    };

    // Debug logging
    console.log(`Order ${order.publicId} - bizInInvoiceURL:`, order.bizInInvoiceURL);

    const colorChange = keyframes`
        0% {
            background-color: red;
        }
        100% {
            background-color: orange;
        }
    `;

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative", // Added for absolute positioning of delete button
            }}
        >
            {isSuperAdmin && (
                <IconButton
                    aria-label="delete order"
                    onClick={handleDeleteDialogOpen}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'error.main',
                        color: 'white',
                        width: 36,
                        height: 36,
                        zIndex: 1,
                        '&:hover': {
                            backgroundColor: 'error.dark',
                        }
                    }}
                    size="small"
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )}
            {/* Ikona generowania AI dla SuperAdmin */}
            {isSuperAdmin && order.isTestGenerationInProgress && (
                <Tooltip title="Trwa generowanie pytań AI - zamówienie jest tymczasowo zablokowane">
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: order.bizInInvoiceURL ? 96 : 52,
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress size={24} color="info" />
                    </Box>
                </Tooltip>
            )}
            {order.bizInInvoiceURL && (
                <Avatar
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: isSuperAdmin ? 52 : 8, // Adjust position based on delete button presence
                        backgroundColor: 'success.main',
                        color: 'white',
                        width: 36,
                        height: 36,
                        zIndex: 1,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'success.dark',
                        }
                    }}
                    onClick={() => window.open(order.bizInInvoiceURL, '_blank')}
                    title="Faktura - Kliknij aby otworzyć"
                >
                    <ReceiptIcon sx={{ fontSize: '16px' }} />
                </Avatar>
            )}
            <CardHeader
                avatar={
                    <Avatar sx={{ bgcolor: getOrderStatusColor(order.orderState) }} title={getOrderStatusTitle(order.orderState)}> {getStatusIcon(order.orderState)}</Avatar>
                }
                title={order.userDisplayName && order.userDisplayName.trim() ? order.userDisplayName : order.userEmail}
                titleTypographyProps={{
                    sx: { fontWeight: 'bold', color: 'secondary' }
                }}
                subheader={order.orderDateTime ? `${formatToLocalDate(order.orderDateTime.toString())} ${formatToLocalTime(order.orderDateTime.toString())}` : 'Brak daty'}
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2">
                    <strong>{order.customeClientServiceName}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                    nazwa wskazana przez Klienta
                </Typography>
                <Typography variant="subtitle2">
                    <strong>#{order.publicId}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                    numer zamówienia
                </Typography>
                <Typography variant="subtitle2" sx={{ color: getOrderStatusColor(order.orderState) }}>
                    {getOrderStatusTitle(order.orderState)}
                </Typography>
                <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                    status zamówienia
                </Typography>
                <Typography>
                    {currencyFormat(order.totalDiscountedPrice)} ( {currencyFormat(order.unitDiscountedPrice)} x {getPolishLiteral(order.totalItem)} )
                </Typography>
                <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                    wartość zamówienia
                </Typography>
                {order.bizInInvoiceURL &&
                    <>
                        <Typography variant="subtitle2"
                            onClick={() => window.open(order.bizInInvoiceURL, '_blank')}
                            title="Faktura - Kliknij aby otworzyć"
                            sx={{
                                cursor: 'pointer',
                                color: 'primary.main',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: 'primary.dark',
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            <strong>Faktura nr {order.accountNumber}</strong>
                        </Typography>
                        <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                            faktura
                        </Typography>
                    </>
                }

                {order.clientComment &&
                    <>
                        <Typography variant="subtitle2">
                            <strong>{order.clientComment}</strong>
                        </Typography>
                        <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                            komentarz/uwagi Klienta
                        </Typography>
                    </>
                }

                <Typography>
                    {order.userPhone}
                </Typography>
                <Typography sx={{ fontSize: 12, mb: 2 }} gutterBottom>
                    nr telefonu podany przez Klienta
                </Typography>
            </CardContent>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                >
                    <Typography>Uczestnicy</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <List>
                        {benefitients.map((benefitient, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={`${benefitient.firstName} ${benefitient.lastName}`} secondary={`Pesel: ${benefitient.pesel}, Miejsce urodzenia: ${benefitient.birthPlace}, Data urodzenia: ${benefitient.birthDate}, Certyfikat: ${benefitient.certId}`} />
                            </ListItem>
                        ))}
                    </List>
                </AccordionDetails>
            </Accordion>
            <CardActions
                disableSpacing
                sx={{
                    mt: "auto",
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 1
                }}
            >
                <Button
                    sx={{
                        mr: 1, width: '100px', height: '40px', ...(order.orderState == 4 && {
                            animation: `${colorChange} 2s infinite`,
                            // Remove default button color when using custom background color animation
                            bgcolor: 'transparent',
                            // Adjust text color to be visible on both orange and red
                            color: 'white',
                        })
                    }}
                    variant='outlined'
                    component={Link} to={`/admin/orders/${order.publicId}`}
                    size="small"
                    title='Więcej'
                >Więcej</Button>
                <Button
                    sx={{ mr: 1, width: '100px', height: '40px' }}
                    variant='outlined'
                    onClick={() => handleCardSelectOrder!(order)}
                    size="small"
                    title='Edycja'
                    color='warning'
                    disabled={order.isTestGenerationInProgress}
                >Edycja</Button>
                <Button
                    sx={{ width: '100px', height: '40px' }}
                    variant='outlined'
                    onClick={handleOpen}
                    size="small"
                    title='Załącz'
                    color='info'
                    disabled={order.isTestGenerationInProgress}
                >
                    Załącz
                </Button>
                {(order.orderState == 2 || order.orderState == 3 || order.orderState == 6 || order.orderState == 7) ? (
                    <Tooltip title={!order.isExamApproved ? 'Testy nie zostały jeszcze zatwierdzone' : ''}>
                        <span>
                            <LoadingButton
                                loading={isGeneratingCertificate}
                                sx={{ mr: 1, height: '40px' }}
                                variant='outlined'
                                onClick={() => handleGenerateCertificates(order.publicId)}
                                size="small"
                                title='Pobierz Certyfikaty'
                                color='success'
                                disabled={!order.isExamApproved || order.isTestGenerationInProgress}
                            >Pobierz Certyfikaty</LoadingButton>
                        </span>
                    </Tooltip>
                ) : ''}
                {/* <Button
                    sx={{ mr: 1, height: '40px' }}
                    variant='outlined'
                    onClick={() => handleCardSelectAddInvoice!(order)}
                    size="small"
                    title='Wystaw FV'
                    color='warning'
                >Wystaw FV</Button> */}
                <Button
                    sx={{ mr: 1, height: '40px' }}
                    variant='outlined'
                    onClick={() => setInvoiceUrlDialogOpen(true)}
                    size="small"
                    title='URL Faktury'
                    color='secondary'
                    disabled={order.isTestGenerationInProgress}
                >URL FV</Button>
                {hasTestData() && (
                    <Tooltip title={order.isTestGenerationInProgress ? "Trwa generowanie pytań AI" : "Pobierz test PDF"}>
                        <span>
                            <LoadingButton
                                loading={isDownloadingTest}
                                sx={{ height: '40px', minWidth: '80px' }}
                                variant='outlined'
                                onClick={handleDownloadTestPdf}
                                size="small"
                                color='primary'
                                startIcon={<CloudDownloadIcon />}
                                disabled={order.isTestGenerationInProgress}
                            >Test</LoadingButton>
                        </span>
                    </Tooltip>
                )}
                {hasAnswerKeyData() && (
                    <Tooltip title={order.isTestGenerationInProgress ? "Trwa generowanie pytań AI" : "Pobierz klucz odpowiedzi PDF"}>
                        <span>
                            <LoadingButton
                                loading={isDownloadingKey}
                                sx={{ height: '40px', minWidth: '80px' }}
                                variant='outlined'
                                onClick={handleDownloadAnswerKeyPdf}
                                size="small"
                                color='secondary'
                                startIcon={<CloudDownloadIcon />}
                                disabled={order.isTestGenerationInProgress}
                            >Klucz</LoadingButton>
                        </span>
                    </Tooltip>
                )}
                {/* Existing upload dialog */}
                <Dialog open={open} onClose={handleClose}>
                    <DialogContent>
                        <AdminOrderScanUploadForm order={order} cancelUpload={handleClose} />
                    </DialogContent>
                </Dialog>

                {/* Delete confirmation dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleDeleteDialogClose}
                    aria-labelledby="alert-dialog-title"
                >
                    <DialogTitle id="alert-dialog-title" sx={{ textAlign: 'left' }}>
                        Potwierdzenie usunięcia zamówienia
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ p: 1 }}>
                            <Typography variant="body1" align="left">
                                Czy na pewno chcesz usunąć zamówienie #{order.publicId}?
                            </Typography>
                            <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }} align="left">
                                Ta operacja jest nieodwracalna!
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                        <Button onClick={handleDeleteDialogClose} variant="outlined">Anuluj</Button>
                        <LoadingButton
                            loading={isDeletingOrder}
                            variant="contained"
                            color="error"
                            onClick={handleDeleteOrder}
                        >
                            Usuń zamówienie
                        </LoadingButton>
                    </DialogActions>
                </Dialog>

                {/* Invoice URL dialog */}
                <AdminOrderInvoiceUrlDialog
                    open={invoiceUrlDialogOpen}
                    onClose={() => setInvoiceUrlDialogOpen(false)}
                    order={order}
                />
            </CardActions>

            {/* Informacja o weryfikacji zamówienia - tylko dla Admin (nie SuperAdmin) */}
            {order.isTestGenerationInProgress && !isSuperAdmin && (
                <Box sx={{ px: 2, pb: 2 }}>
                    <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                            Zamówienie jest weryfikowane
                        </Typography>
                        <Typography variant="caption">
                            Proces potrwa około minuty. Strona odświeży się automatycznie. Po tym czasie będziesz mógł zarządzać zamówieniem.
                        </Typography>
                        <LinearProgress sx={{ mt: 1 }} />
                    </Alert>
                </Box>
            )}
        </Card>
    )
}